import { gql, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useGroupRules, UserGroupRules } from 'src/graphql/entities/group-rules';
import { generateFieldModifiersMap } from 'src/graphql/utils';
import { useNotifications } from 'src/utils/hooks';
import { updateCachedPrimaryProjectId } from './project.cache';

type RestoreDeletedProjectResponse = {
  restoreDeletedProject: {
    projectId: string;
  };
};

export const RESTORE_DELETED_PROJECT_MUTATION = gql`
  mutation RestoreDeletedProject($params: RestoreDeletedProjectInput!) {
    restoreDeletedProject(params: $params) {
      projectId
    }
  }
`;

export const useRestoreProject = () => {
  const { showSuccessAlert, showErrorAlert } = useNotifications();
  const { groupRules } = useGroupRules();
  const [restoreProjectMutation] = useMutation<RestoreDeletedProjectResponse>(
    RESTORE_DELETED_PROJECT_MUTATION,
    {
      update: (cache, result) => {
        const projectId = result.data?.restoreDeletedProject?.projectId;
        if (!projectId) {
          return;
        }
        cache.modify({
          id: cache.identify({ __typename: 'Project', id: projectId }),
          fields: generateFieldModifiersMap({
            deletedAt: null,
            __typename: 'Project',
          }),
        });
        // the backend makes the restored project primary on a site that has none
        updateCachedPrimaryProjectId(
          cache,
          projectId,
          (primaryProjectId) => primaryProjectId ?? projectId,
        );
      },
      onCompleted: () => {
        showSuccessAlert('Project was restored');
      },
      onError: () => {
        showErrorAlert(`Could not restore project`);
      },
    },
  );

  const restoreProject = useCallback(
    async (projectId: string) => {
      if (groupRules[UserGroupRules.AccessToRestoreDeletedProjects]) {
        await restoreProjectMutation({
          variables: {
            params: {
              projectId,
            },
          },
        });
      }
    },
    [groupRules, restoreProjectMutation],
  );

  return {
    restoreProject,
  };
};
